import { useApolloClient, useMutation } from "@apollo/client";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PersonAdd from "@mui/icons-material/PersonAdd";
import PersonRemove from "@mui/icons-material/PersonRemove";
import { Box, Button, Link, Paper, Stack, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DiscrepancyBubblesCell } from "components/DiscrepancyBubblesCell";
import { parseOmnisearch, stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  StaffManifestReviewManifestsQuery,
  StaffManifestReviewManifestsQueryVariables,
} from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DiscrepancyReviewDialog } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog";
import { ManifestCommentsPopover } from "routes/Customer/Waste/Manifests/ManifestCommentsPopover";
import { ManifestDetails } from "routes/Customer/Waste/Manifests/ManifestDetails";
import { ManifestReviewDialog } from "routes/Customer/Waste/Manifests/ManifestReviewDialog";
import { DocumentReviewPermalink } from "routes/Customer/Waste/Manifests/ReviewPermalink";
import { SourcesCell } from "routes/Customer/Waste/Manifests/SourcesCell";
import { relativeLogTime } from "util/dates";

import { useBreadcrumb } from "../../../../hooks/useBreadcrumbs";
import { AssignDialog } from "../AssignDialog";
import {
  ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION,
  STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY,
  assigneeLabel,
  type ManifestQuickLink,
  type ManifestReviewRow,
} from "./api";
import { ManifestDetailDialog } from "./ManifestDetailDialog";
import {
  DocumentReviewCell,
  ManifestReviewEManifestCell,
  StaffActionsRequiredCell,
} from "./ManifestReviewCells";
import { ManifestReviewSummary } from "./ManifestReviewSummary";
import { TenantFeatureFlagsProvider } from "./TenantFeatureFlagsProvider";

/** Two-line column header (the renderHeader ignores its params). */
const twoLineHeader = (line1: string, line2: string) =>
  function TwoLineHeader() {
    return (
      <Box
        sx={{
          lineHeight: 1.2,
          whiteSpace: "normal",
          fontWeight: 500,
          fontSize: "0.8125rem",
        }}
      >
        {line1}
        <br />
        {line2}
      </Box>
    );
  };

// "Since delegated" should always read as elapsed time ("3 weeks ago"), never flip to a
// bare date. relativeLogTime falls back to an absolute date past its cutoff, so set the
// cutoff effectively to never (100 years) to keep the whole column relative.
const ALWAYS_RELATIVE_CUTOFF_HOURS = 24 * 365 * 100;

// Structured filters shown in the omnisearch popover. Keys match what the resolver
// parses (tenant / status / assignee). All column-derived text filters are excluded so
// the popover shows only these dropdown/type-ahead controls; free-text matching of
// manifest #, tenant, facility, and EPA id is handled by the omnisearch box itself.
// Outstanding-action filter values (must match the resolver's staffAction keys) → labels.
const STAFF_ACTION_LABELS: Record<string, string> = {
  docReview: "Document Review",
  discrepancyReview: "Discrepancy Review",
  vendorContact: "Vendor Contact",
  exceptionReport: "Exception Report",
  epaCorrections: "EPA Corrections",
};

// Data-source facets for the multi-select Source filter (values match the resolver).
const SOURCE_LABELS: Record<string, string> = {
  eManifest: "e-Manifest",
  vendor: "Vendor",
  document: "Document",
};

// Document review states for the multi-select Document Review filter.
const DOC_REVIEW_STATE_LABELS: Record<string, string> = {
  NEEDS_REVIEW: "Needs Review",
  NEEDS_REFRESH: "New Version",
  REVIEWED: "Reviewed",
  NOT_PROCESSED: "Not Processed",
};

// Discrepancy lifecycle states for the Field / EPA status dropdowns ("Any" = no value).
const DISCREPANCY_STATUS_OPTIONS = ["UNRESOLVED", "PENDING", "RESOLVED"];
const DISCREPANCY_STATUS_LABELS: Record<string, string> = {
  UNRESOLVED: "Unresolved",
  PENDING: "Pending",
  RESOLVED: "Resolved",
};

// e-Manifest compliance states (values match the resolver / EMANIFEST_COMPLIANCE_FILTERS).
const EMANIFEST_COMPLIANCE_LABELS: Record<string, string> = {
  tsdfContactRequired: "TSDF Contact Required",
  tsdfContacted: "TSDF Contacted",
  exceptionRequired: "Exception Required",
  exceptionFiled: "Exception Filed",
};

// NB: a filter `key` that exactly matches a grid column `field` (e.g. "documentReview",
// "eManifestCompliance") loses its enumValues to the column definition, leaving an empty
// dropdown — so those filters use distinct keys ("documentReviewState", "compliance").
const MANIFEST_REVIEW_FILTERS: FilterKey[] = [
  { key: "manifestNumber", header: "Manifest #", filterKeyType: "text" },
  { key: "tenant", header: "Tenant", filterKeyType: "multipleTenant" },
  { key: "facility", header: "Facility", filterKeyType: "text" },
  {
    key: "staffAction",
    header: "Staff actions required",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(STAFF_ACTION_LABELS),
    enumPresentationFunction: (value) => STAFF_ACTION_LABELS[value] ?? value,
  },
  {
    // Multi-select; the resolver treats multiple sources as AND (the manifest must
    // have ALL chosen sources on file), mirroring the E/V/D pills (ENG-15798).
    key: "source",
    header: "Sources",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(SOURCE_LABELS),
    enumPresentationFunction: (value) => SOURCE_LABELS[value] ?? value,
  },
  {
    key: "documentReviewState",
    header: "Document Review",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.keys(DOC_REVIEW_STATE_LABELS),
    enumPresentationFunction: (value) => DOC_REVIEW_STATE_LABELS[value] ?? value,
  },
  {
    key: "fieldDiscrepancy",
    header: "Field discrepancies",
    filterKeyType: "enum",
    enumValues: DISCREPANCY_STATUS_OPTIONS,
    enumPresentationFunction: (value) => DISCREPANCY_STATUS_LABELS[value] ?? value,
  },
  {
    key: "epaCorrection",
    header: "EPA requested corrections",
    filterKeyType: "enum",
    enumValues: DISCREPANCY_STATUS_OPTIONS,
    enumPresentationFunction: (value) => DISCREPANCY_STATUS_LABELS[value] ?? value,
  },
  {
    key: "missingEManifest",
    header: "Missing e-Manifest",
    filterKeyType: "boolean",
  },
  {
    key: "compliance",
    header: "e-Manifest compliance",
    filterKeyType: "enum",
    enumValues: Object.keys(EMANIFEST_COMPLIANCE_LABELS),
    enumPresentationFunction: (value) => EMANIFEST_COMPLIANCE_LABELS[value] ?? value,
  },
  { key: "shippedDate", header: "Shipped date", filterKeyType: "date" },
  { key: "inTransit", header: "In transit (days)", filterKeyType: "number" },
  {
    key: "assignee",
    header: "Assignee",
    filterKeyType: "multipleStaffAnyUser",
  },
  // Duration filter: days since the manifest's latest open assignment (>/</= days).
  { key: "assignedAt", header: "Assigned (days ago)", filterKeyType: "number" },
];

// Column fields whose auto-generated free-text filters we suppress (replaced by the
// structured filters above, or covered by free-text search).
const MANIFEST_REVIEW_EXCLUDED_FILTERS = [
  "manifestTrackingNumber",
  "tenantName",
  "facilityName",
  "shippedDate",
  "sources",
  "documentReview",
  "encampDiscrepancies",
  "epaCorrections",
  "eManifestCompliance",
  "staffActionRequired",
  "status",
  "type",
  "assignee",
  "assignedAt",
  "assignedBy",
];

// Queue + scoreboard queries to refresh after any assignment write (or failed attempt).
const ASSIGNMENT_REFETCH_QUERIES = ["StaffManifestReviewManifests", "ManifestReviewSummary"];

export function ManifestReviewPage() {
  useBreadcrumb({
    label: "Manifest Review",
    to: "/staff/fulfillment/waste/manifest-review",
  });
  const { success, error } = useAlerts();

  const [searchParams, setSearchParams] = useSearchParams();

  const writeOmnisearch = (next: Record<string, string[]>) => {
    const str = stringifyOmnisearch(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (str) {
          params.set("omnisearch", str);
        } else {
          params.delete("omnisearch");
        }
        return params;
      },
      { replace: true }
    );
  };

  // Upsert/remove several keyed terms at once (clickable summary boxes / reviewer rows);
  // a null value clears that key. Preserves free text + untouched terms.
  const applyOmnisearch = (updates: Record<string, string[] | null>) => {
    const next = { ...parseOmnisearch(searchParams.get("omnisearch") ?? "") };
    for (const [key, value] of Object.entries(updates)) {
      if (value && value.length > 0) {
        next[key] = value;
      } else {
        delete next[key];
      }
    }
    writeOmnisearch(next);
  };

  // Current keyed omnisearch terms — lets the summary tiles render their on/off state.
  const activeTerms = useMemo(
    () => parseOmnisearch(searchParams.get("omnisearch") ?? ""),
    [searchParams]
  );

  // Toggle a single keyed value on/off (summary scoreboard tiles). Multi-value keys
  // (e.g. staffAction) add/remove just this value so several tiles combine; `exclusive`
  // keys (assignee, missingEManifest) set or clear the whole key.
  const toggleFilter = (key: string, value: string, opts?: { exclusive?: boolean }) => {
    const next = { ...parseOmnisearch(searchParams.get("omnisearch") ?? "") };
    const existing: string[] = next[key] ?? [];
    const isActive = existing.includes(value);
    if (opts?.exclusive) {
      if (isActive) delete next[key];
      else next[key] = [value];
    } else if (isActive) {
      const remaining = existing.filter((entry) => entry !== value);
      if (remaining.length > 0) next[key] = remaining;
      else delete next[key];
    } else {
      next[key] = [...existing, value];
    }
    writeOmnisearch(next);
  };

  const [selectedRows, setSelectedRows] = useState<ManifestReviewRow[]>([]);
  // Drive the grid's selection from page state (not just mirror it): the grid caches
  // selected row objects across refetches (cross-page selection), so without this a row
  // that no longer exists after a refetch would stay silently selected and poison the
  // next bulk assignment with its stale id.
  const selectedIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  // Rows targeted by the assign picker (a single row from ＋/the modal, or the selection).
  const [assignTargets, setAssignTargets] = useState<ManifestReviewRow[]>([]);
  const [detailRow, setDetailRow] = useState<ManifestReviewRow | null>(null);
  // In-place quick link (manifest drawer / document review / discrepancy dialog) opened
  // from a row's links + staff-action icons, instead of navigating to the customer page.
  const [quickLink, setQuickLink] = useState<ManifestQuickLink | null>(null);
  const openQuickLink = useCallback((link: ManifestQuickLink) => setQuickLink(link), []);

  const apolloClient = useApolloClient();
  const [assignManifests, { loading: assigning }] = useMutation(
    ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION,
    { refetchQueries: ASSIGNMENT_REFETCH_QUERIES }
  );

  const applyAssignment = useCallback(
    async (rows: ManifestReviewRow[], staffUserId: string | null) => {
      try {
        const { data } = await assignManifests({
          variables: {
            processedManifestIds: rows.map((row) => row.processedManifestId),
            staffUserId,
          },
        });
        const result = data?.assignManifestReviewManifests;
        const itemCount =
          (result?.assignedDiscrepancyCount ?? 0) + (result?.assignedDocumentReviewCount ?? 0);
        if (itemCount === 0) {
          // Every item on these manifests was resolved/reviewed since the page loaded —
          // nothing left to assign; the refetch reconciles the rows.
          success("Nothing to assign — these manifests have no active review items");
          return;
        }
        const verb = staffUserId ? "Assigned" : "Unassigned";
        success(`${verb} ${rows.length} manifest${rows.length === 1 ? "" : "s"}`);
      } catch {
        // Most likely the queue changed under the page (e.g. a manifest no longer
        // exists) — refresh it so the next attempt operates on current rows. Await the
        // refetch: if it also fails the grid may be stale, so tell the user to reload
        // rather than leaving them with a "try again" toast over un-refreshed rows.
        try {
          await apolloClient.refetchQueries({
            include: ASSIGNMENT_REFETCH_QUERIES,
          });
          error("Failed to update assignment — refreshed the queue, please try again");
        } catch {
          error(
            "Failed to update assignment and couldn't refresh the queue — please reload the page"
          );
        }
      }
    },
    [assignManifests, apolloClient, success, error]
  );

  const openAssignFor = useCallback((rows: ManifestReviewRow[]) => {
    if (rows.length === 0) return;
    setAssignTargets(rows);
    setAssignDialogOpen(true);
  }, []);

  const handleAssign = async (staffUserId: string) => {
    await applyAssignment(assignTargets, staffUserId);
    setAssignDialogOpen(false);
    setAssignTargets([]);
    setSelectedRows([]);
  };

  const rawColumns: OmnisearchGridColDef<ManifestReviewRow>[] = useMemo(
    () => [
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        width: 170,
        renderCell: ({ row }) => (
          <Link
            component="button"
            type="button"
            underline="hover"
            color="inherit"
            onClick={(e) => {
              e.stopPropagation();
              openQuickLink({
                kind: "drawer",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
              });
            }}
            sx={{ font: "inherit" }}
          >
            {row.manifestTrackingNumber}
          </Link>
        ),
      },
      { field: "tenantName", headerName: "Tenant", flex: 1, minWidth: 140 },
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 1,
        minWidth: 140,
        valueGetter: (_value, row) => row.facilityName ?? row.facilityEpaId ?? "—",
      },
      {
        field: "sources",
        headerName: "Sources",
        width: 96,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => <SourcesCell availableSources={row.availableSources} />,
      },
      {
        field: "shippedDate",
        headerName: "Shipped Date",
        width: 130,
        // Hidden by default (see initialColumnVisibilityModel); UTC medium format to
        // match the customer Manifests grid.
        valueFormatter: (value) => (value ? getUtcDisplayDate(value, "med") : "—"),
      },
      {
        field: "staffActionRequired",
        headerName: "Staff Actions Required",
        // Fixed width that just fits the five circled icons (no flex grow).
        width: 168,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <StaffActionsRequiredCell row={row} onOpenQuickLink={openQuickLink} />
        ),
      },
      {
        field: "documentReview",
        headerName: "Document Review",
        renderHeader: twoLineHeader("Document", "Review"),
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <DocumentReviewCell
            state={row.documentReviewState}
            onClick={() =>
              openQuickLink({
                kind: "docReview",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                documentIds: row.documentIds,
              })
            }
          />
        ),
      },
      {
        field: "encampDiscrepancies",
        headerName: "Field Discrepancies",
        renderHeader: twoLineHeader("Field", "Discrepancies"),
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
        // Bubble style; orange = unresolved, blue = pending, green = resolved (field-level only).
        renderCell: ({ row }) => (
          <DiscrepancyBubblesCell
            unresolved={row.encampUnresolved ?? 0}
            pending={row.encampPending ?? 0}
            resolved={row.encampResolved ?? 0}
            unresolvedColor="warning"
            pendingColor="info"
            onClick={() =>
              openQuickLink({
                kind: "discrepancies",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                tab: "field",
              })
            }
          />
        ),
      },
      {
        field: "epaCorrections",
        headerName: "EPA Requested Corrections",
        renderHeader: twoLineHeader("EPA Requested", "Corrections"),
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
        // Bubble style (same as Field); red = unresolved, blue = pending, green = resolved.
        // Counts are EPA_CORRECTION_REQUEST-kind only — missing-manifest discrepancies
        // are surfaced in the e-Manifest Compliance column, not counted here.
        renderCell: ({ row }) => (
          <DiscrepancyBubblesCell
            unresolved={row.epaUnresolved ?? 0}
            pending={row.epaPending ?? 0}
            resolved={row.epaResolved ?? 0}
            unresolvedColor="error"
            pendingColor="info"
            onClick={() =>
              openQuickLink({
                kind: "discrepancies",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                tab: "compliance",
              })
            }
          />
        ),
      },
      {
        field: "eManifestCompliance",
        headerName: "e-Manifest Compliance",
        renderHeader: twoLineHeader("e-Manifest", "Compliance"),
        width: 130,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <ManifestReviewEManifestCell
            row={row}
            onClick={() =>
              openQuickLink({
                kind: "discrepancies",
                processedManifestId: row.processedManifestId,
                tenantId: row.tenantId,
                tab: "compliance",
              })
            }
          />
        ),
      },
      {
        field: "assignee",
        headerName: "Assignee",
        flex: 1,
        minWidth: 140,
        // Server sort is keyed by column; assignee/assignedBy resolve to a name in JS and
        // aren't server-sortable, so don't offer a no-op sort affordance on them.
        sortable: false,
        // Empty assignee renders as "—" (a tooltip clarifies); filter for it via the
        // Unassigned quick-toggle or assignee:unassigned.
        valueGetter: (_value, row) => (row.assignee ? assigneeLabel(row.assignee) : "—"),
      },
      {
        field: "assignedAt",
        headerName: "Assigned",
        width: 130,
        valueGetter: (_value, row) =>
          row.assignedAt ? relativeLogTime(row.assignedAt, ALWAYS_RELATIVE_CUTOFF_HOURS) : "—",
      },
      {
        field: "assignedBy",
        headerName: "Assigned by",
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: (_value, row) => (row.assignedBy ? assigneeLabel(row.assignedBy) : "—"),
      },
      {
        // Compact staff-comments affordance: the orange chat-bubble (with its
        // count) opens the same comment-thread popover used on the manifest
        // drawer + review dialogs. Hidden by default (see
        // initialColumnVisibilityModel). The count comes from the page query's
        // batched `commentCount` (one DataLoader query per page), so cells don't
        // each fire their own request.
        field: "comments",
        headerName: "Comments",
        width: 96,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <ManifestCommentsPopover
            processedManifestId={row.processedManifestId}
            count={row.commentCount}
          />
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: (params: GridRowParams<ManifestReviewRow>) =>
          params.row.assignee
            ? [
                <GridActionsCellItem
                  key="unassign"
                  icon={
                    <Tooltip title="Unassign">
                      <PersonRemove />
                    </Tooltip>
                  }
                  label="Unassign"
                  onClick={() => applyAssignment([params.row], null)}
                />,
              ]
            : [
                <GridActionsCellItem
                  key="assign"
                  icon={
                    <Tooltip title="Assign">
                      <PersonAdd />
                    </Tooltip>
                  }
                  label="Assign"
                  onClick={() => openAssignFor([params.row])}
                />,
              ],
      },
    ],
    [applyAssignment, openAssignFor, openQuickLink]
  );

  // Bulk assign/unassign live in the grid toolbar, to the right of Manage Columns
  // (the All/Mine/Unassigned quick filters now live in the summary scoreboard).
  // Unassign isn't a destructive action, so it stays the normal primary blue.
  const bulkActionButtons = [
    <Button
      key="assign"
      variant="contained"
      size="small"
      disabled={assigning || selectedRows.length === 0}
      onClick={() => openAssignFor(selectedRows)}
    >
      {selectedRows.length > 0 ? `Assign (${selectedRows.length})` : "Assign"}
    </Button>,
    <Tooltip key="unassign" title="Remove assignment">
      <span>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          disabled={assigning || selectedRows.length === 0}
          startIcon={<DeleteOutline />}
          onClick={async () => {
            await applyAssignment(selectedRows, null);
            setSelectedRows([]);
          }}
        >
          Unassign
        </Button>
      </span>
    </Tooltip>,
  ];

  return (
    <Stack spacing={3} sx={{ px: 3, py: 3 }}>
      <ManifestReviewSummary
        onApplyFilter={applyOmnisearch}
        onToggle={toggleFilter}
        onClearAll={() => writeOmnisearch({})}
        activeTerms={activeTerms}
      />

      <Paper variant="outlined" sx={{ p: 3 }}>
        <OmnisearchDataGrid<
          StaffManifestReviewManifestsQuery,
          StaffManifestReviewManifestsQueryVariables,
          ManifestReviewRow
        >
          persistenceKey="manifest-review-manifests-v1"
          isURLDriven
          // Paper owns the padding; suppress the omnisearch bar's own horizontal padding.
          withPadding={false}
          // Structured filters only (Tenant type-ahead, Status dropdown, Assignee
          // type-ahead); suppress the per-column free-text filters.
          additionalFilterColumns={MANIFEST_REVIEW_FILTERS}
          excludeFilterColumns={MANIFEST_REVIEW_EXCLUDED_FILTERS}
          commandButtons={bulkActionButtons}
          columns={rawColumns}
          // Default view leads with Staff Actions Required; the detail columns
          // (sources, ship date, document review, the two discrepancy columns,
          // e-Manifest compliance) and "Delegated by" are hidden by default and
          // available via Manage Columns.
          initialColumnVisibilityModel={{
            sources: false,
            shippedDate: false,
            documentReview: false,
            encampDiscrepancies: false,
            epaCorrections: false,
            eManifestCompliance: false,
            assignedBy: false,
            comments: false,
          }}
          dataQuery={STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY}
          getItems={(data) => data.staffManifestReviewManifests.items}
          getCount={(data) => data.staffManifestReviewManifests.totalCount}
          checkboxSelection
          disableRowSelectionOnClick
          selectedIds={selectedIds}
          onSelectedRowsChanged={setSelectedRows}
          onRowClick={(params) => setDetailRow(params.row)}
          initialPageSize={50}
          rowHeight="auto"
        />
      </Paper>

      <AssignDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onAssign={handleAssign}
        title="Assign to Staff"
        selectedCount={assignTargets.length}
        isLoading={assigning}
      />

      {detailRow && (
        <ManifestDetailDialog
          open
          onClose={() => setDetailRow(null)}
          mode="staff"
          processedManifestId={detailRow.processedManifestId}
          manifestTrackingNumber={detailRow.manifestTrackingNumber}
          facilityName={detailRow.facilityName}
          staffDetails={{
            tenantName: detailRow.tenantName,
            facilityEpaId: detailRow.facilityEpaId,
            status: detailRow.status,
            assignee: detailRow.assignee,
            documentIds: detailRow.documentIds,
            onAssign: () => {
              openAssignFor([detailRow]);
              setDetailRow(null);
            },
          }}
        />
      )}

      {/* In-place quick links opened from a row (no navigation): the manifest drawer,
          the document-review modal, or the discrepancy dialog on a specific tab. The
          queue is cross-tenant, so we pass the row's tenant explicitly to the data
          queries AND wrap in TenantFeatureFlagsProvider so the flag-gated sections
          render against the row's tenant (not the staff user's session tenant). */}
      {quickLink && (
        <TenantFeatureFlagsProvider tenantId={quickLink.tenantId}>
          {quickLink.kind === "drawer" && (
            <ManifestDetails
              open
              processedManifestId={quickLink.processedManifestId}
              tenantId={quickLink.tenantId}
              onClose={() => setQuickLink(null)}
            />
          )}
          {quickLink.kind === "docReview" &&
            // Fast path: open straight from the row's needs-review document ids (the
            // dialog resolves the manifest from the doc). Only when there are none
            // (e.g. an already-reviewed manifest) fall back to resolving via the
            // heavier processed-manifest fetch.
            (quickLink.documentIds.length > 0 ? (
              <ManifestReviewDialog
                documentIds={quickLink.documentIds}
                onClose={() => setQuickLink(null)}
              />
            ) : (
              <DocumentReviewPermalink
                processedManifestId={quickLink.processedManifestId}
                tenantId={quickLink.tenantId}
                onClose={() => setQuickLink(null)}
              />
            ))}
          {quickLink.kind === "discrepancies" && (
            <DiscrepancyReviewDialog
              processedManifestIds={[quickLink.processedManifestId]}
              tenantId={quickLink.tenantId}
              initialTab={quickLink.tab}
              onClose={() => setQuickLink(null)}
            />
          )}
        </TenantFeatureFlagsProvider>
      )}
    </Stack>
  );
}
